///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Switch, useLocation } from "react-router-dom";

import Home from "./Home";
import NewBid from "./NewBid";
import PendingBids from "./PendingBids";
import Admin from "./Admin";
import ProtectedRoute from "../components/ProtectedRoute";
import Search from "./Search";
import Bid from "./Bid";
import InvalidRoute from "../components/InvalidRoute";
import NavBar from "../components/NavBar";
import { fetchUser } from "../slices/userSlice";
import SystemError from "../components/SystemError";
import { AccessDenied } from "../components/AccessDenied";
import { ForbiddenAccess } from "../components/ForbiddenAccess";
import { fetchFeatureFlags } from "../slices/featureFlagsSlice";
import { fetchSupportedFiles, clearForbiddenError } from "../slices/appSlice";
import { closeInitialLoadErrorModal } from "../slices/bidSummarySlice";
import Notifications from "./Notifications";
import {
  fetchNewNotfications,
  fetchNotifications,
  fetchNotificationSettings
} from "../slices/notificationsSlice";
import Settings from "./Settings";
import PricingAnalytics from "./PricingAnalytics";
import LocationSearchModal from "../components/LocationSearchModal";
import initializePendo from "../helpers/initializePendo";
import { division, userRole } from "../helpers/types";
import { fetchStrategicQuantile } from "../slices/strategicQuantileSlice";

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isFirstRender = useRef(true);
  const [isLocationSearchOpen, setIsLocationSearchOpen] = useState(false);
  const user = useSelector(state => state.user);
  const { supportedFiles, isForbiddenError } = useSelector(state => state.app);
  const { notifications, settings } = useSelector(state => state.notifications);
  const {
    isFetching: featureFlagsFetching,
    isFailure: featureFlagsFailure
  } = useSelector(state => state.featureFlags);
  const {
    isFetching: strategicQuantileFetching,
    isFailure: strategicQuantileFailure
  } = useSelector(state => state.strategicQuantile);

  useEffect(() => {
    const notificationsPollingInterval = 15000;
    if (user.user.userId === null && !user.isFetching && !user.isFailure) {
      const callback = () => {
        setInterval(() => {
          dispatch(fetchNewNotfications());
        }, notificationsPollingInterval);
      };
      dispatch(fetchUser(initializePendo));
      dispatch(fetchNotifications(callback));
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (
      supportedFiles.supportedFiles.length === 0 &&
      !supportedFiles.isFetching &&
      !supportedFiles.isFailure
    ) {
      dispatch(fetchSupportedFiles());
    }
  }, [dispatch, supportedFiles]);

  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission();
    }
  }, []);

  const handleReloadPage = () => {
    if (user.isFailure) {
      dispatch(fetchUser());
    }
    if (supportedFiles.isFailure) {
      dispatch(fetchSupportedFiles());
    }
    if (settings.isFailure) {
      dispatch(fetchNotificationSettings());
    }
    if (strategicQuantileFailure) {
      dispatch(fetchStrategicQuantile());
    }
  };

  useEffect(() => {
    dispatch(fetchFeatureFlags());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchStrategicQuantile());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(clearForbiddenError());
    dispatch(closeInitialLoadErrorModal());
  }, [location.pathname, dispatch]);

  const isFailure =
    user.isFailure ||
    supportedFiles.isFailure ||
    featureFlagsFailure ||
    strategicQuantileFailure;
  const isFetching =
    user.isFetching ||
    user.user.userId === null ||
    supportedFiles.isFetching ||
    supportedFiles === null ||
    featureFlagsFetching ||
    strategicQuantileFetching;
  const systemError =
    isFailure && !isForbiddenError && !user.isFetching && !supportedFiles.isFetching;

  return (
    <>
      {!isFailure && !isFetching && user.user.role !== 0 && (
        <>
          <NavBar
            role={user.user.role}
            isAdmin={user.user.isAdmin}
            userDivision={user.user.division}
          notifications={notifications}
            onOpenLocationSearch={() => setIsLocationSearchOpen(true)}
            isLocationSearchOpen={isLocationSearchOpen}
          />
          <LocationSearchModal
            key={isLocationSearchOpen}
            open={isLocationSearchOpen}
            handleClose={() => setIsLocationSearchOpen(false)}
          />
        </>
      )}
      <main id="main" className="flex-auto" data-testid="main">
        {!user.isFailure && !user.isFetching && user.user.role === 0 && (
          <AccessDenied />
        )}
        {!user.isFailure &&
          !user.isFetching &&
          user.user.role !== 0 &&
          isForbiddenError && <ForbiddenAccess />}
        {systemError && <SystemError reloadPage={handleReloadPage} />}
        {!systemError && !isForbiddenError && user.user.role !== 0 && (
          <Switch>
            <ProtectedRoute
              path="/pending-bids"
              component={PendingBids}
              isLoading={isFetching}
            />
            <ProtectedRoute
              path="/search"
              component={Search}
              isLoading={isFetching}
            />
            <ProtectedRoute
              path="/new-bid"
              isLoading={isFetching}
              render={routeProps =>
                user.user.division === division.eu ? (
                  <Redirect to="/" />
                ) : (
                  <NewBid {...routeProps} />
                )
              }
            />
            {user.user.isAdmin && (
              <ProtectedRoute
                path="/admin"
                component={Admin}
                isLoading={isFetching}
              />
            )}
            <ProtectedRoute
              path="/bid/:bidId"
              isLoading={isFetching}
              render={props => (
                <Bid
                  role={
                    user.user.role === userRole.management
                      ? user.user.userRoleForManagementUser
                      : user.user.role
                  }
                  userId={user.user.userId}
                  division={user.user.division}
                  isManagement={user.user.role === userRole.management}
                  {...props}
                />
              )}
            />
            <ProtectedRoute
              path="/notifications"
              isLoading={isFetching}
              component={Notifications}
            />
            <ProtectedRoute
              path="/settings"
              isLoading={isFetching}
              component={Settings}
            />
            <ProtectedRoute
              path="/analytics"
              isLoading={isFetching}
              component={PricingAnalytics}
            />
            <ProtectedRoute
              exact
              path={["/", "/grid-view"]}
              component={Home}
              isLoading={isFetching}
            />
            <ProtectedRoute component={InvalidRoute} isLoading={isFetching} />
          </Switch>
        )}
      </main>
    </>
  );
};

export default App;
